import type { TFunction } from 'i18next'
import type { Task } from '@/src/renderer/lib/db/models'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import { dateDiffDays, formatStartCollapsedBadge } from './parseStartDate'
import { formatDuration } from './parseDuration'

type Translate = (key: string, options?: Record<string, unknown>) => string

/** Ein Tag kurz: „heute“, „morgen“, sonst Wochentag oder Datum — wie die Abzeichen in der Zeile. */
function dayLabel(date: Date, locale: string, t: Translate, now: Date): string {
  const diff = dateDiffDays(date, now)
  if (diff === 0) return t('quickAdd.today')
  if (diff === 1) return t('quickAdd.tomorrow')
  return formatStartCollapsedBadge(date, locale, now)
}

/**
 * **Die zweite Zeile des Toasts nach dem Anlegen** (Sven, 04.10.2026): Ort und Bereich, dazu was gesetzt
 * wurde — Wann, Deadline, Dauer, Tags. „Demnächst · Arbeit · morgen · bis Fr · 10m · #Garten“.
 */
export function quickAddCreatedDetails(
  task: Task,
  { place, tagNames, locale, t, now = new Date() }: { place: string; tagNames: string[]; locale: string; t: Translate; now?: Date },
): string {
  const parts = [place]
  if (task.startDate) parts.push(dayLabel(task.startDate, locale, t, now))
  if (task.dueDate) parts.push(t('quickAdd.dueOn', { date: dayLabel(task.dueDate, locale, t, now) }))
  if (task.estimatedDuration) parts.push(formatDuration(task.estimatedDuration, task.durationMode, t('taskCreation.durationDayUnit')))
  for (const name of tagNames) parts.push(`#${name}`)
  return parts.join(' · ')
}

/**
 * **„Blumen gießen“ angelegt** — darunter Ort und Angaben, „Anzeigen“ springt hin wie ein Treffer der
 * Suche (`MainLayout`, Ereignis `open-task`). Nach dem Anlegen in der Schnelleingabe (Sven, 04.10.2026).
 * Mit `focusAction` hat „Anzeigen“ den Fokus: Enter springt hin.
 */
export function showQuickAddCreatedToast(t: TFunction, task: Task, details: string, { focusAction = false }: { focusAction?: boolean } = {}): void {
  showSuccessToast({
    message: t('quickAdd.createdTitle', { title: task.title }),
    description: details,
    action: { label: t('common.show'), onClick: () => dispatchAppEvent('open-task', { task }) },
    focusAction,
  })
}
